import type { TFunction } from "i18next";

import {
  formatLifecycleActorText,
  isLifecycleActivityLogRow,
} from "./monitoringLifecycleText";
import type {
  MonitoringActivityLogRun,
  MonitoringRunChange,
  MonitoringRunDetail,
} from "./types";

export function isEmptyMonitoringChangeValue(value: string): boolean {
  const trimmed = value.trim();
  if (!trimmed) {
    return true;
  }
  const lower = trimmed.toLowerCase();
  return (
    lower === "none" || lower === "false" || lower === "null" || trimmed === "—"
  );
}

export function formatMonitoringChangeValue(
  value: string,
  t: TFunction
): string {
  if (isEmptyMonitoringChangeValue(value)) {
    return t("—");
  }
  return value;
}

export function quoteMonitoringChangeValue(
  value: string,
  t: TFunction
): string {
  return `'${formatMonitoringChangeValue(value, t)}'`;
}

export type MonitoringRunChangeDisplay =
  | { kind: "no_value"; message: string }
  | { kind: "change"; prefix: string; toValue: string };

export function getMonitoringRunChangeDisplay(
  change: MonitoringRunChange,
  t: TFunction
): MonitoringRunChangeDisplay {
  if (isEmptyMonitoringChangeValue(change.new_value)) {
    return {
      kind: "no_value",
      message: t("No value returned for '{{label}}'", { label: change.label }),
    };
  }

  return {
    kind: "change",
    prefix: t("{{label}} changed from {{from}} to", {
      label: change.label,
      from: quoteMonitoringChangeValue(change.old_value, t),
    }),
    toValue: quoteMonitoringChangeValue(change.new_value, t),
  };
}

export function getVisibleMonitoringRunChanges(
  changes: MonitoringRunChange[]
): MonitoringRunChange[] {
  return changes.filter((change) => change.old_value !== change.new_value);
}

export function formatMonitoringRunChangeText(
  change: MonitoringRunChange,
  t: TFunction
): string {
  const display = getMonitoringRunChangeDisplay(change, t);
  if (display.kind === "no_value") {
    return display.message;
  }
  return `${display.prefix} ${display.toValue}`;
}

export function getActivityLogChangeAttributeItems(
  run: MonitoringActivityLogRun,
  t: TFunction
): string[] {
  const visibleChanges = getVisibleMonitoringRunChanges(run.changes);
  if (visibleChanges.length > 0) {
    return visibleChanges.map((change) =>
      formatMonitoringRunChangeText(change, t)
    );
  }
  if (isLifecycleActivityLogRow(run)) {
    const actorText = formatLifecycleActorText(run);
    return [actorText ?? t("—")];
  }
  const expirySummary = (
    run as MonitoringActivityLogRun & {
      change_attribute_summary?: string | null;
    }
  ).change_attribute_summary;
  if (expirySummary) {
    return [expirySummary];
  }
  return [t("—")];
}

export function getActivityLogChangeAttributeSummary(
  run: MonitoringActivityLogRun,
  t: TFunction
): string {
  return getActivityLogChangeAttributeItems(run, t).join("; ");
}

export function hasMonitoringRunDetailContent(
  detail: MonitoringRunDetail
): boolean {
  return (
    getVisibleMonitoringRunChanges(detail.changes).length > 0 ||
    detail.alerts.length > 0
  );
}

export function getMeaningfulMonitoringRunDetails(
  details: readonly MonitoringRunDetail[]
): MonitoringRunDetail[] {
  return details.filter(hasMonitoringRunDetailContent);
}

export function getPendingResultTooltipMessage(
  monitorStatus: string | null,
  nextScheduledRunDate: string | null,
  formatDate: (date: string) => string,
  t: TFunction
): string {
  if (nextScheduledRunDate) {
    const formatted = formatDate(nextScheduledRunDate);
    if (formatted) {
      return t("Next scheduled run: {{date}}", { date: formatted });
    }
  }

  switch (monitorStatus) {
    case "ACTIVE":
      return t("No run has taken place yet.");
    case "PAUSED":
      return t("This applicant is paused.");
    case "NOT_ENROLLED":
      return t("This applicant is not enrolled.");
    case "DEACTIVATED":
      return t("This applicant is deactivated.");
    case "ENDED":
      return t("Monitoring for this applicant has ended.");
    default:
      return t("No run has taken place yet.");
  }
}
